import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Award,
  Search,
  Filter,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { registrationAPI, coursesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import Pagination from '../common/Pagination';

const CourseRegistrationsManagement = () => {
  const [registrations, setRegistrations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });
  const [filters, setFilters] = useState({
    status: '',
    courseId: '',
    search: ''
  });

  useEffect(() => {
    fetchRegistrations();
    fetchCourses();
  }, [pagination.currentPage, pagination.limit]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      // Create query params from filters and pagination
      const queryParams = {
        page: pagination.currentPage,
        limit: pagination.limit
      };
      if (filters.status) queryParams.status = filters.status;
      if (filters.courseId) queryParams.courseId = filters.courseId;
      if (filters.search) queryParams.search = filters.search;
      
      
      const response = await registrationAPI.getAll(queryParams);
      
      // Ensure registrations is always an array
      const registrationsData = Array.isArray(response.data.data) ? response.data.data : 
                              (response.data.registrations || []);
      setRegistrations(registrationsData);
      
      // Update pagination information
      if (response.data.pagination) {
        setPagination({
          currentPage: response.data.pagination.currentPage || 1,
          totalPages: response.data.pagination.totalPages || 1,
          totalItems: response.data.pagination.totalItems || registrationsData.length,
          limit: response.data.pagination.limit || pagination.limit
        });
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load registrations');
      setRegistrations([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await coursesAPI.getAll();
      
      // Handle different response structures
      let coursesData = [];
      if (Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        coursesData = response.data.data;
      } else if (response.data.data && response.data.data.courses && Array.isArray(response.data.data.courses)) {
        coursesData = response.data.data.courses;
      } else if (response.data.courses && Array.isArray(response.data.courses)) {
        coursesData = response.data.courses;
      }
      
      setCourses(coursesData);
    } catch (error) {
      setCourses([]); // Set empty array on error
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    fetchRegistrations();
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      courseId: '',
      search: ''
    });
    // Reset to first page when filters are reset
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    setTimeout(fetchRegistrations, 0);
  };
  
  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };
  
  const handleLimitChange = (limit) => {
    setPagination(prev => ({
      ...prev,
      limit,
      currentPage: 1 // Reset to first page when changing limit
    }));
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      setLoading(true);
      
      // Apply visual feedback before API call
      const updatedRegistrations = registrations.map(reg => {
        if (reg.id === id) {
          // Create a visual transition effect by adding a temporary class
          const element = document.getElementById(`registration-row-${id}`);
          if (element) {
            element.classList.add('bg-gray-100', 'transition-all', 'duration-500');
            setTimeout(() => {
              element.classList.remove('bg-gray-100');
            }, 1000);
          }
          return { ...reg, status: status };
        }
        return reg;
      });
      
      // Update UI immediately for responsive feel
      setRegistrations(updatedRegistrations);
      
      // Then make the actual API call
      await registrationAPI.updateStatus(id, status);
      
      // Refresh data to ensure consistency with server
      fetchRegistrations();
      
      // Enhanced toast notification with custom styling based on status
      const statusIcons = {
        'pending': '⏳',
        'confirmed': '✅',
        'cancelled': '❌',
        'completed': '🏆'
      };
      
      toast.success(
        `${statusIcons[status]} Status updated to ${status.charAt(0).toUpperCase() + status.slice(1)}`,
        {
          duration: 4000,
          style: {
            background: status === 'pending' ? '#FEF9C3' : 
                       status === 'confirmed' ? '#DCFCE7' : 
                       status === 'cancelled' ? '#FEE2E2' : 
                       '#DBEAFE',
            color: status === 'pending' ? '#854D0E' : 
                  status === 'confirmed' ? '#166534' : 
                  status === 'cancelled' ? '#991B1B' : 
                  '#1E40AF',
            border: '1px solid',
            borderColor: status === 'pending' ? '#FDE68A' : 
                        status === 'confirmed' ? '#BBF7D0' : 
                        status === 'cancelled' ? '#FECACA' : 
                        '#BFDBFE',
            borderRadius: '8px',
            fontWeight: 'bold'
          }
        }
      );
    } catch (error) {
        console.error('Error updating status:', error);
        toast.error('Failed to update registration status', {
          duration: 4000,
          style: {
            background: '#FEE2E2',
            color: '#991B1B',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            fontWeight: 'bold'
          }
        });
        // Revert to original data on error
        fetchRegistrations();
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegistration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    
    try {
      await registrationAPI.delete(id);
      setRegistrations(registrations.filter(reg => reg.id !== id));
      toast.success('Registration deleted successfully');
    } catch (error) {
      console.error('Error deleting registration:', error);
      toast.error('Failed to delete registration');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'confirmed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Course Registrations</h2>
          <p className="text-gray-600 dark:text-gray-300">Manage and track all course registrations</p>
        </div>
        <button onClick={fetchRegistrations} className="btn btn-primary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange} className="input w-full">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Course</label>
            <select name="courseId" value={filters.courseId} onChange={handleFilterChange} className="input w-full">
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search</label>
            <div className="relative">
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by name or email"
                className="input w-full pl-10"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={applyFilters} className="btn btn-primary flex-1">
              <Filter className="w-4 h-4 mr-2" />
              Apply Filters
            </button>
            <button onClick={resetFilters} className="btn btn-outline">Reset</button>
          </div>
        </div>
      </div>

      {/* Registrations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : registrations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-4">
            <div className="text-gray-400 mb-4">
              <Clock className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No registrations found</h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md">
              {filters.status || filters.courseId || filters.search 
                ? 'Try changing your filters or search criteria'
                : 'There are no course registrations yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Registration Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {registrations.map((registration) => (
                    <motion.tr 
                      key={registration.id}
                      id={`registration-row-${registration.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {registration.user?.firstName} {registration.user?.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {registration.user?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{registration.course?.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{registration.course?.courseType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(registration.registrationDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(registration.status)}`}>
                        {registration.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{registration.contactPhone}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{registration.contactEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <div className="relative inline-block text-left">
                          <div>
                            <button 
                              type="button" 
                              className={`inline-flex items-center justify-center w-full px-3 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500 transition-all duration-200
                                ${registration.status === 'pending' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 
                                  registration.status === 'confirmed' ? 'bg-green-100 text-green-800 hover:bg-green-200' : 
                                  registration.status === 'cancelled' ? 'bg-red-100 text-red-800 hover:bg-red-200' : 
                                  'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
                              id={`status-menu-button-${registration.id}`}
                              aria-expanded="true"
                              aria-haspopup="true"
                              onClick={() => {
                                const menuId = `status-menu-${registration.id}`;
                                const menu = document.getElementById(menuId);
                                if (menu) {
                                  menu.classList.toggle('hidden');
                                }
                              }}
                            >
                              {registration.status === 'pending' ? <Clock className="w-4 h-4 mr-2" /> : 
                               registration.status === 'confirmed' ? <CheckCircle className="w-4 h-4 mr-2" /> : 
                               registration.status === 'cancelled' ? <XCircle className="w-4 h-4 mr-2" /> : 
                               <Award className="w-4 h-4 mr-2" />}
                              {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                            </button>
                          </div>
                          <div 
                            id={`status-menu-${registration.id}`}
                            className="hidden origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                            role="menu" 
                            aria-orientation="vertical" 
                            aria-labelledby={`status-menu-button-${registration.id}`}
                            tabIndex="-1"
                          >
                            <div className="py-1" role="none">
                              <button
                                onClick={() => {
                                  handleUpdateStatus(registration.id, 'pending');
                                  document.getElementById(`status-menu-${registration.id}`).classList.add('hidden');
                                }}
                                className="flex items-center px-4 py-2 text-sm w-full text-left text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900 dark:text-yellow-400"
                                role="menuitem"
                                tabIndex="-1"
                              >
                                <Clock className="w-4 h-4 mr-3" />
                                Pending
                              </button>
                              <button
                                onClick={() => {
                                  handleUpdateStatus(registration.id, 'confirmed');
                                  document.getElementById(`status-menu-${registration.id}`).classList.add('hidden');
                                }}
                                className="flex items-center px-4 py-2 text-sm w-full text-left text-green-600 hover:bg-green-100 dark:hover:bg-green-900 dark:text-green-400"
                                role="menuitem"
                                tabIndex="-1"
                              >
                                <CheckCircle className="w-4 h-4 mr-3" />
                                Confirmed
                              </button>
                              <button
                                onClick={() => {
                                  handleUpdateStatus(registration.id, 'cancelled');
                                  document.getElementById(`status-menu-${registration.id}`).classList.add('hidden');
                                }}
                                className="flex items-center px-4 py-2 text-sm w-full text-left text-red-600 hover:bg-red-100 dark:hover:bg-red-900 dark:text-red-400"
                                role="menuitem"
                                tabIndex="-1"
                              >
                                <XCircle className="w-4 h-4 mr-3" />
                                Cancelled
                              </button>
                              <button
                                onClick={() => {
                                  handleUpdateStatus(registration.id, 'completed');
                                  document.getElementById(`status-menu-${registration.id}`).classList.add('hidden');
                                }}
                                className="flex items-center px-4 py-2 text-sm w-full text-left text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 dark:text-blue-400"
                                role="menuitem"
                                tabIndex="-1"
                              >
                                <Award className="w-4 h-4 mr-3" />
                                Completed
                              </button>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteRegistration(registration.id)}
                          className="p-2 rounded-full text-red-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900 transition-colors duration-200"
                          title="Delete Registration"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
              </table>
              
              {/* Pagination Component */}
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                limit={pagination.limit}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                className="mt-4"
              />
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseRegistrationsManagement;